/**
 * The only real top-level scalar fields on the GraphQL `EmployeeImportInput` type. Every other
 * import field -- including built-ins like Email, First Name, and Last Name -- is an ordinary
 * company attribute, not a core field.
 */
export type EmployeeImportCoreField = 'employeeId' | 'enabled' | 'languageId' | 'role';

/**
 * Where a mapping or transformation's value ends up: either one of the four core fields, or a
 * company attribute (built-in or custom) identified by id, carrying its own resolved name since
 * this package has no database access to look it up itself. Shared by both column mappings and
 * custom transformations -- both target the same kind of place in an employee record.
 */
export type MappingTarget =
  | { targetCoreField: EmployeeImportCoreField; targetAttributeId?: undefined; targetAttributeName?: undefined }
  | { targetCoreField?: undefined; targetAttributeId: number; targetAttributeName: string | null };

/**
 * Where a column mapping's value ends up: a `MappingTarget` (core field or attribute), or
 * `excluded: true` when the raw column should be dropped entirely rather than renamed or kept --
 * e.g. a column a company's export still sends but WorkTango no longer wants persisted at all.
 * Transformations have no equivalent case; every transformation always computes a value.
 */
export type ColumnMappingTarget =
  | MappingTarget
  | { targetCoreField?: undefined; targetAttributeId?: undefined; targetAttributeName?: undefined; excluded: true };

/**
 * A single per-company mapping from a raw HRIS export column header to a WorkTango core field,
 * attribute, or exclusion. `sourceColumnName` is matched case-insensitively against a parsed
 * row's keys.
 */
export type ColumnMapping = ColumnMappingTarget & {
  sourceColumnName: string;
};

/**
 * A parsed row from an employee import file, keyed by column header. Values are strings or
 * null -- never re-typed (dates, numbers, booleans) at this stage.
 */
export type ImportRow = Record<string, string | null>;

/**
 * Resolves a mapping or transformation's target down to the single row key its value should be
 * written under -- a core field name as-is, or an attribute's name lowercased, matching how
 * `applyColumnMappings` already keys its output. Returns null when targeting an attribute that has
 * no name for the requesting user's language: there's no key to address it by, so the caller skips
 * that mapping or transformation entirely rather than writing under a made-up key.
 */
export function resolveTargetKey(target: MappingTarget): string | null {
  return target.targetCoreField ?? target.targetAttributeName?.toLowerCase() ?? null;
}

// ---------------------------------------------------------------------------------------------
// Custom transformations (WT-31261)
// ---------------------------------------------------------------------------------------------

/**
 * One part of a Concatenate transformation. `source` is a raw column name, read from the same
 * full row every transformation receives -- including columns that are never mapped or
 * persisted as their own attribute (e.g. a birthday column used only to derive another value).
 */
export type ConcatenatePart = {
  source: string;
  /** How to format the part's raw value before joining. Defaults to `'verbatim'` when omitted. */
  format?: 'verbatim' | 'extractMonthDay';
  /**
   * Used in place of the formatted value when this part's raw column is empty or missing --
   * without this, a Concatenate with an optional input would silently contribute an empty
   * string instead of a deliberate fallback (e.g. Deckers' EmployeeIDMMDD falls back to `'0'`
   * when Date of Birth is absent).
   */
  default?: string;
};

export type ConcatenateDefinition = {
  parts: ConcatenatePart[];
  /** Joins the formatted parts together. Defaults to `''` when omitted. */
  separator?: string;
};

/** A post-selection format applied only to the value FirstNonNull actually selects. */
export type FirstNonNullFormat = { type: 'zeroPad'; length: number };

export type FirstNonNullDefinition = {
  /** Raw column names, checked in order; the first with a non-empty value wins. */
  candidates: string[];
  format?: FirstNonNullFormat;
};

export type ConditionalMatch =
  | { mode: 'equals' | 'startsWith'; value: string }
  | { mode: 'in'; value: string[] };

/** Either a literal value, or another raw column's value, read at resolution time. */
export type ConditionalOutcome = { type: 'fixed'; value: string | null } | { type: 'source'; source: string };

export type ConditionalDefinition = {
  source: string;
  match: ConditionalMatch;
  whenTrue: ConditionalOutcome;
  whenFalse: ConditionalOutcome;
};

export type ReplaceDefinition = {
  source: string;
  /**
   * Always interpreted as a regular expression, and must contain an anchor (`^` or `$`) --
   * enforced by `validateTransformationDefinition`, not just convention. A bare, unanchored
   * substring pattern could silently corrupt an unrelated value that happens to contain it
   * mid-string (e.g. fixing a truncated `.co` domain must not also mangle `example.co.uk`).
   */
  pattern: string;
  replacement: string;
};

export type FixedValueDefinition = { value: string | null };

export type TenureRangeDefinition = {
  /** A start-date column (`YYYY-MM-DD`), binned into a fixed set of tenure-length ranges. */
  source: string;
};

/**
 * Which shape `GenerationCohortDefinition.source` holds -- real HRIS exports vary: some send a
 * full birth date, some send only a birth year, and some (e.g. MGIC) send a pre-computed age
 * instead of a birth date at all.
 */
export type GenerationCohortSourceType = 'birthDate' | 'birthYear' | 'age';

export type GenerationCohortDefinition = {
  source: string;
  sourceType: GenerationCohortSourceType;
};

/**
 * A single configured custom transformation, discriminated by `transformType`. Each variant's
 * `definition` is validated by `validateTransformationDefinition` before it's ever stored, so
 * `applyTransformation` can assume it's already well-formed.
 */
export type Transformation = MappingTarget &
  (
    | { transformType: 'CONCATENATE'; definition: ConcatenateDefinition }
    | { transformType: 'FIRST_NON_NULL'; definition: FirstNonNullDefinition }
    | { transformType: 'CONDITIONAL'; definition: ConditionalDefinition }
    | { transformType: 'REPLACE'; definition: ReplaceDefinition }
    | { transformType: 'FIXED_VALUE'; definition: FixedValueDefinition }
    | { transformType: 'TENURE_RANGE'; definition: TenureRangeDefinition }
    | { transformType: 'GENERATION_COHORT'; definition: GenerationCohortDefinition }
  );

export type TransformationValidationResult = { valid: true; errors: [] } | { valid: false; errors: string[] };
